import React from "react";

import Button from "@common/components/Button/Button";
import { ModalProps } from "@common/components/Modal/Modal";
import SelectDropdown, { Option } from "@common/components/SelectDropdown/SelectDropdown";
import { forwardingOptions, getIntegrationTypeName, IntegrationType } from "@common/models/integrations";
import { Notion, NotionOption } from "@common/models/integrations/Notion.model";
import { IntegrationLayoutSettings } from "@main/pages/Administration/components/ModalBody/UserFeedbackIntegrations/components/IntegrationLayoutSettings";
import { getIntegrationTypeLogo } from "@main/pages/Administration/util/getIntegrationTypeLogo";

import { Form } from "../ModalBody.styles";
import { useNotionIntegrationFormConsumer } from "./consumers/useNotionIntegrationFormConsumer";

interface NotionIntegrationModalProps {
  Modal: React.ComponentType<ModalProps>;
  modalProps: ModalProps;
  integration?: Notion;
  selectedAppId: string;
  onFinish: () => void;
}

export const NotionIntegrationModal = ({
  Modal,
  modalProps,
  integration,
  selectedAppId,
  onFinish,
}: NotionIntegrationModalProps) => {
  const {
    disabled,
    loading,
    databaseOption,
    databaseOptions,
    forwardingOption,
    handleFormChange,
    handleFormSubmit,
    integrationLayout,
    moveIntegrationLayoutEntryUp,
    moveIntegrationLayoutEntryDown,
    toggleIntegrationLayoutEntry,
    metadataKeys,
    metadataEnabledState,
    toggleMetadataKey,
    toggleAllMetadataKeys,
    setMetadataKeysEnabled,
    resetMetadataKeys,
    hasMetadataKeyChanges,
  } = useNotionIntegrationFormConsumer({ integration, selectedAppId, onFinish });

  const optionDidChange = (option: Option, name: string | undefined) => {
    name && handleFormChange(name, option);
  };

  const SaveButton = () => {
    const isButtonDisabled =
      checkIsButtonDisabled(disabled, databaseOption, forwardingOption, integrationLayout, integration) &&
      !hasMetadataKeyChanges;

    return (
      <Button
        size="small"
        tooltip={{
          position: "top",
          text: "No changes have been made",
          disabled: !isButtonDisabled || disabled,
        }}
        disabled={isButtonDisabled || loading}
        loadingText={loading && "Saving..."}
        onClick={handleFormSubmit}
      >
        Save changes
      </Button>
    );
  };

  return (
    <Modal
      {...modalProps}
      key={IntegrationType.NOTION}
      buttonElement={<SaveButton />}
      heading={`${getIntegrationTypeName(IntegrationType.NOTION)} integration`}
      iconPath={getIntegrationTypeLogo(IntegrationType.NOTION)}
    >
      <Form
        noValidate
        onSubmit={(event) => event.preventDefault()}
      >
        <SelectDropdown
          name="forwardingOption"
          value={forwardingOption}
          options={forwardingOptions}
          placeholder="Please choose"
          label="Forward tickets"
          onChange={optionDidChange}
          isSearchable={false}
          disabled={disabled || loading}
          hasSubtitles
        />
        <SelectDropdown
          name="databaseOption"
          value={databaseOption}
          options={databaseOptions}
          placeholder="Please choose"
          label="Database"
          onChange={optionDidChange}
          isSearchable={true}
          disabled={disabled || loading}
        />

        <IntegrationLayoutSettings
          integrationLayout={integrationLayout}
          moveEntryUp={moveIntegrationLayoutEntryUp}
          moveEntryDown={moveIntegrationLayoutEntryDown}
          toggleEntry={toggleIntegrationLayoutEntry}
          metadataKeys={metadataKeys}
          metadataEnabledState={metadataEnabledState}
          toggleMetadataKey={toggleMetadataKey}
          toggleAllMetadataKeys={toggleAllMetadataKeys}
          setMetadataKeysEnabled={setMetadataKeysEnabled}
          resetMetadataKeys={resetMetadataKeys}
        />
      </Form>
    </Modal>
  );
};

const checkIsButtonDisabled = (
  disabled: boolean,
  databaseOption: Option,
  forwardingOption: Option,
  integrationLayout: any[],
  integration?: Notion,
) => {
  if (disabled) return true;
  if (!databaseOption || !forwardingOption) return true;
  if (!databaseOption.value || databaseOption.value === "") return true;

  try {
    const parsedDatabaseOption = JSON.parse(databaseOption.value) as NotionOption;
    if (
      integration &&
      (parsedDatabaseOption.databaseId !== integration?.database_id ||
        !!forwardingOption.value !== integration?.automatic_forward)
    ) {
      return false;
    }
  } catch (error) {
    // Failed to parse database option, keep button disabled
    return true;
  }

  // Check if integration layout has changed
  return !(integration && hasIntegrationLayoutChanged(integrationLayout, integration.integration_layout));
};

const hasIntegrationLayoutChanged = (currentLayout: any[], originalLayout?: any[]): boolean => {
  if (!originalLayout || originalLayout.length === 0) return currentLayout.length > 0;
  if (currentLayout.length !== originalLayout.length) return true;

  return currentLayout.some((entry, index) => {
    const originalEntry = originalLayout[index];
    return entry.key !== originalEntry.key || entry.enabled !== originalEntry.enabled;
  });
};
